import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';

export interface SeoConfig {
  title: string;
  description: string;
  image: string;
  url?: string;
}

@Injectable({ providedIn: 'root' })
export class SeoService {
  private meta = inject(Meta);
  private titleService = inject(Title);
  private platformId = inject(PLATFORM_ID);
  private router = inject(Router);
  private readonly SITE_BASE = 'https://reliefapplications.org';

  private readonly DEFAULT: SeoConfig = {
    title: 'Relief Applications – Technology for nonprofits',
    description: 'Experts in data management and collection solutions and app development for nonprofit organizations.',
    image: '/assets/images/logos/reliefapps-sharing-web.jpg',
  };

  update(config: Partial<SeoConfig>): void {
    const merged: SeoConfig = { ...this.DEFAULT, ...config };
    const image = this.toAbsoluteUrl(merged.image);
    const url = merged.url ?? this.currentHref();

    this.titleService.setTitle(merged.title);
    this.meta.updateTag({ name: 'description', content: merged.description });
    this.meta.updateTag({ property: 'og:title', content: merged.title });
    this.meta.updateTag({ property: 'og:description', content: merged.description });
    this.meta.updateTag({ property: 'og:image', content: image });
    this.meta.updateTag({ property: 'og:url', content: url });
    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:title', content: merged.title });
    this.meta.updateTag({ name: 'twitter:description', content: merged.description });
    this.meta.updateTag({ name: 'twitter:image', content: image });
  }

  reset(): void {
    this.update(this.DEFAULT);
  }

  stripHtml(html: string): string {
    if (!isPlatformBrowser(this.platformId)) {
      return html.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
    }
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return (tmp.textContent || tmp.innerText || '').replace(/\s+/g, ' ').trim();
  }

  private currentHref(): string {
    if (isPlatformBrowser(this.platformId)) return window.location.href;
    return `${this.SITE_BASE}${this.router.url}`;
  }

  private toAbsoluteUrl(url: string): string {
    if (!url || typeof url !== 'string') return `${this.SITE_BASE}${this.DEFAULT.image}`;
    if (url.startsWith('http')) return url;
    const origin = isPlatformBrowser(this.platformId)
      ? window.location.origin
      : this.SITE_BASE;
    return `${origin}${url.startsWith('/') ? '' : '/'}${url}`;
  }
}
